/**
 * ChefVoiceControls - Voice interaction controls for Chef features
 * 
 * Provides push-to-talk, pause/resume, and visual feedback for voice interactions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Heading, Text } from './ui';
import { MicManager } from '../services/speech';

const ChefVoiceControls = ({ 
    speechManager,
    isEnabled = false,
    onToggleEnabled,
    onTranscriptReceived,
    currentRecipe = null,
    compact = false,
    className = '',
    setMicOn
}) => {
    const [isListening, setIsListening] = useState(false);
    const [isMicOn, setIsMicOn] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [currentTranscript, setCurrentTranscript] = useState('');
    const [transcriptHistory, setTranscriptHistory] = useState([]);
    const [showTranscriptModal, setShowTranscriptModal] = useState(false);
    const [pendingTranscript, setPendingTranscript] = useState(null);
    const [micStatus, setMicStatus] = useState({ refCount: 0, audioContextState: 'suspended' });

    // Monitor mic status
    useEffect(() => {
        const updateMicStatus = () => {
            const state = MicManager.getState();
            setMicStatus({
                refCount: state.referenceCount,
                audioContextState: state.audioContextState
            });
        };

        updateMicStatus();
        const interval = setInterval(updateMicStatus, 100); // Update 10 times per second
        return () => clearInterval(interval);
    }, []);

    // Update listening state when speech manager state changes
    useEffect(() => {
        if (!speechManager) return;

        const handleStateChange = (stateChange) => {
            setIsListening(stateChange.current === 'listening');
        };

        speechManager.setOnStateChange(handleStateChange);
        
        return () => {
            speechManager.setOnStateChange(null);
        };
    }, [speechManager]);

    // Handle speech results
    useEffect(() => {
        if (!speechManager) return;
        // Subscribe to manager events instead of overriding handler
        const onFinal = (result) => {
            const transcriptText = result.transcript || '';
            if (!transcriptText) return;
            const timestamp = Date.now();
            const transcriptEntry = {
                id: `transcript_${timestamp}`,
                text: transcriptText,
                timestamp,
                recipeId: currentRecipe?.id || null,
                recipeName: currentRecipe?.title || null,
                isConfirmed: false
            };
            setPendingTranscript(transcriptEntry);
            setShowTranscriptModal(true);
            setCurrentTranscript('');
        };

        speechManager.on && speechManager.on('final', onFinal);
        return () => {
            speechManager.off && speechManager.off('final', onFinal);
        };
    }, [speechManager, currentRecipe]);

    // Handle microphone toggle
    const handleMicToggle = useCallback(async () => {
        if (!speechManager || isPaused) return;
        
        const newMicState = !isMicOn;
        setIsMicOn(newMicState);
        
        if (newMicState) {
            await speechManager.start();
        } else {
            // Use hardStop to guarantee mic is fully turned off
            await speechManager.hardStop('user_toggle_off');
            // Update mic state immediately after hard stop
            const state = MicManager.getState();
            if (setMicOn) {
                setMicOn(state.isAcquired && state.audioContextState === 'running');
            }
        }
    }, [speechManager, isPaused, isMicOn, setMicOn]);

    // Handle pause/resume
    const handleTogglePause = useCallback(async () => {
        if (!speechManager) return;
        
        const newPaused = !isPaused;
        setIsPaused(newPaused);
        
        if (newPaused) {
            // Use hardStop to guarantee mic off
            await speechManager.hardStop('user_toggle_off');
            setIsMicOn(false);
        } else if (isMicOn) {
            speechManager.start();
        }
    }, [speechManager, isPaused, isMicOn]);

    // Handle transcript confirmation
    const handleConfirmTranscript = useCallback(() => {
        if (!pendingTranscript) return;
        
        const confirmedTranscript = {
            ...pendingTranscript,
            isConfirmed: true
        };
        
        setTranscriptHistory(prev => [confirmedTranscript, ...prev.slice(0, 9)]); // Keep last 10
        setShowTranscriptModal(false);
        setPendingTranscript(null);
        
        if (onTranscriptReceived) {
            onTranscriptReceived(confirmedTranscript);
        }
    }, [pendingTranscript, onTranscriptReceived]);

    // Handle transcript rejection
    const handleRejectTranscript = useCallback(() => {
        setShowTranscriptModal(false);
        setPendingTranscript(null);
    }, []);

    // Get listening status text
    const getListeningStatus = () => {
        if (isPaused) return 'Paused';
        if (isMicOn) return 'Microphone On';
        if (isListening) return 'Listening';
        return 'Ready';
    };

    // Get listening status color
    const getListeningStatusColor = () => {
        if (isPaused) return '#666';
        if (isMicOn) return '#1b263b';
        if (isListening) return '#22c55e';
        return '#666';
    };

    if (!isEnabled) {
        return (
            <div className={`${className}`}>
                <button
                    onClick={onToggleEnabled}
                    className={`${compact ? 'px-3 py-1 text-xs' : 'px-4 py-2 text-sm'} bg-neutral-900 text-neutral-0 hover:opacity-60 transition-opacity font-mono uppercase tracking-wider`}
                >
                    {compact ? 'Enable Voice' : 'Enable Chef Voice'}
                </button>
            </div>
        );
    }

    return (
        <>
            <div className={`${className} ${compact ? 'space-y-2' : 'space-y-3'}`}>
                {/* Status Display */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <div 
                            className={`${compact ? 'w-2 h-2' : 'w-3 h-3'} rounded-full transition-colors`}
                            style={{ backgroundColor: getListeningStatusColor() }}
                        />
                        <Text className={`font-mono ${compact ? 'text-xs' : 'text-sm'} uppercase tracking-wider text-neutral-900`}>
                            {getListeningStatus()}
                        </Text>
                    </div>
                    
                    <button
                        onClick={onToggleEnabled}
                        className={`text-neutral-900 hover:opacity-60 transition-opacity font-mono ${compact ? 'text-xs' : 'text-sm'} uppercase tracking-wider`}
                    >
                        Disable
                    </button>
                </div>

                {/* Microphone Toggle Button */}
                <div className={compact ? 'space-y-1' : 'space-y-2'}>
                    <button
                        onClick={handleMicToggle}
                        disabled={isPaused}
                        className={`w-full ${compact ? 'py-2' : 'py-4'} font-mono ${compact ? 'text-xs' : 'text-sm'} uppercase tracking-wider transition-all ${
                            isMicOn
                                ? 'bg-red-600 text-white hover:bg-red-700'
                                : isPaused
                                ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                                : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                        }`}
                    >
                        {isPaused ? 'Paused' : isMicOn ? 'Turn Off Mic' : 'Turn On Mic'}
                    </button>
                    
                    {!compact && (
                        <Text className="text-xs text-neutral-900 opacity-60 text-center">
                            {isMicOn ? 'Microphone is active - speak now' : 'Click to activate microphone'}
                        </Text>
                    )}
                </div>

                {/* Mic Status Indicator */}
                <div className="flex items-center justify-center mb-2">
                    <div className={`w-2 h-2 rounded-full ${
                        micStatus.refCount > 0 && micStatus.audioContextState === 'running' 
                            ? 'bg-red-500 animate-pulse' 
                            : 'bg-gray-300'
                    }`} />
                    <span className="ml-2 text-xs font-mono uppercase tracking-wider text-neutral-600">
                        Mic {micStatus.refCount > 0 && micStatus.audioContextState === 'running' ? 'ON' : 'OFF'}
                    </span>
                </div>

                {/* Pause/Resume Toggle */}
                {!compact && (
                    <button
                        onClick={handleTogglePause}
                        className={`w-full py-2 font-mono text-sm uppercase tracking-wider transition-colors ${
                            isPaused
                                ? 'bg-neutral-900 text-neutral-0 hover:opacity-60'
                                : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                        }`}
                    >
                        {isPaused ? 'Resume Chef' : 'Pause Chef'}
                    </button>
                )}

                {/* Current Transcript */}
                {!compact && currentTranscript && (
                    <div className="bg-neutral-50 hairline p-3">
                        <Text className="font-mono text-xs uppercase tracking-wider text-neutral-900 mb-1">
                            Current
                        </Text>
                        <Text className="text-sm text-neutral-900">
                            {currentTranscript}
                        </Text>
                    </div>
                )}

                {/* Transcript History */}
                {!compact && transcriptHistory.length > 0 && (
                    <div className="space-y-2">
                        <Text className="font-mono text-xs uppercase tracking-wider text-neutral-900">
                            Recent Transcripts
                        </Text>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                            {transcriptHistory.slice(0, 3).map((transcript) => (
                                <div key={transcript.id} className="bg-neutral-50 hairline p-2">
                                    <Text className="text-xs text-neutral-900 opacity-60">
                                        {new Date(transcript.timestamp).toLocaleTimeString()}
                                    </Text>
                                    <Text className="text-sm text-neutral-900">
                                        {transcript.text}
                                    </Text>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Transcript Confirmation Modal */}
            <Modal isOpen={showTranscriptModal} onClose={handleRejectTranscript}>
                <div className="max-w-2xl mx-auto">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <Heading level={2} style={{ color: '#1b263b' }}>
                                Confirm Transcript
                            </Heading>
                            <Text className="text-neutral-900 opacity-60 mt-1">
                                Review and confirm this voice command
                            </Text>
                        </div>
                        <button
                            onClick={handleRejectTranscript}
                            className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0"
                        >
                            ✕
                        </button>
                    </div>

                    {pendingTranscript && (
                        <div className="space-y-4">
                            {/* Transcript Content */}
                            <div className="bg-neutral-50 hairline p-4">
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                    Voice Command
                                </Text>
                                <Text className="text-lg text-neutral-900">
                                    "{pendingTranscript.text}"
                                </Text>
                            </div>

                            {/* Context Information */}
                            <div className="bg-neutral-50 hairline p-4">
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                    Context
                                </Text>
                                <div className="space-y-1">
                                    <Text className="text-sm text-neutral-900">
                                        <strong>Time:</strong> {new Date(pendingTranscript.timestamp).toLocaleString()}
                                    </Text>
                                    {pendingTranscript.recipeName && (
                                        <Text className="text-sm text-neutral-900">
                                            <strong>Recipe:</strong> {pendingTranscript.recipeName}
                                        </Text>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-between">
                                <button
                                    onClick={handleRejectTranscript}
                                    className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                >
                                    Discard
                                </button>
                                <button
                                    onClick={handleConfirmTranscript}
                                    className="px-4 py-2 bg-neutral-900 text-neutral-0 hover:opacity-60 transition-opacity font-mono text-sm uppercase tracking-wider"
                                >
                                    Confirm & Save
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default ChefVoiceControls;
